import {
  AddIcon,
  CopyIcon,
  DeleteIcon,
  RepeatIcon,
  SettingsIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Progress,
  Text,
} from "@chakra-ui/react";
import api from "api/api";
import {
  ModalCampanhaWhatsApp,
  openModalCampanhaWhatsApp,
} from "components/campanha-components/modal-campanha-whatsapp";
import DynamicTable from "components/dynamic-table";
import { RowStatus } from "components/dynamic-table/row-status";
import { GetLayout } from "components/get-layout";
import { IconButtonWithLoading } from "components/mailing-components/mailing-table/icon-button-with-loading";
import { openModalConfirm } from "components/modal-confirm-new";
import { useEffect, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa";
import { Toast } from "components/toast";
import { InputField } from "components/input-field";

export const CampanhaNova = () => {
  const [loading, setLoading] = useState(false);
  const [campanhas, setCampanhas] = useState([]);
  const [link, setLink] = useState("");
  const [loadingLink, setLoadingLink] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await loadCampanhas();
      setLoading(false);
    };
    load();
  }, []);

  const getCampanhaStatus = (row) => {
    let status;
    if (isNotConfigurada(row)) {
      status = "CONFIGURACAO PENDENTE";
    } else {
      let value = (row.totalCompleted / row.total) * 100;
      status =
        value === 100
          ? "FINALIZADA"
          : isPlaying(row)
          ? "EM ANDAMENTO"
          : "PAUSADA";
    }
    return status;
  };

  const play = async (rowData) => {
    setLoading(true);
    await api.get("/campanhas-nova/" + rowData.id + "/play");
    await loadCampanhas();
    setLoading(false);
  };

  const pause = async (rowData) => {
    setLoading(true);
    await api.get("/campanhas-nova/" + rowData.id + "/pause");
    await loadCampanhas();
    setLoading(false);
  };

  const loadCampanhas = async () => {
    try {
      const response = await api.get("/campanhas-nova");
      setCampanhas(response.data);
    } catch (e) {
      Toast({
        title: "Erro ao carregar campanhas",
        status: "error",
      });
    }
  };

  const atualizar = async () => {
    setLoading(true);
    await loadCampanhas();
    setLoading(false);
  };

  const openEditCampanhaDialog = async (row) => {
    try {
      const { data } = await api.get(`/campanhas-nova/${row.id}`);
      openModalCampanhaWhatsApp(data);
    } catch (error) {
      Toast({
        title: "Não foi possível abrir a campanha!",
        status: "error",
      });
    }
  };

  const isNotConfigurada = (campanha) => {
    return campanha.mensagem == null || campanha.mensagem?.trim() === "";
  };

  const isPlaying = (campanha) => {
    return campanha.paused != null && campanha.paused === false;
  };

  const isFinalizada = (campanha) => {
    return campanha.total === campanha.totalCompleted;
  };

  const acceptDeleteCampanha = async (rowData) => {
    setLoading(true);
    try {
      await api.delete("/campanhas-nova/" + rowData.id);
    } catch (error) {
      Toast({
        title: "Erro ao excluir campanha!",
        status: "error",
      });
    }
    await loadCampanhas();
    setLoading(false);
  };

  const confirmDeleteCampanha = (rowData) => {
    openModalConfirm({
      message: `Deseja excluir "${rowData.nome}" ?`,
      onConfirm: () => acceptDeleteCampanha(rowData),
      confirmButtonStyle: {
        variant: "danger",
        leftIcon: <DeleteIcon />,
        loadingText: "Excluindo",
      },
    });
  };

  const getLink = async () => {
    setLoadingLink(true);
    try {
      const { data } = await api.get("/customers/url-fone");
      setLink(data.url);
    } catch (error) {
    } finally {
      setLoadingLink(false);
    }
  };

  useEffect(() => {
    getLink();
  }, []);

  const columns = [
    {
      name: "Nome",
      key: "nome",
    },
    {
      name: "Criação",
      key: "criacao",
      render: (row) =>
        row.criacao ? new Date(row.criacao).toLocaleString() : null,
    },
    {
      name: "Atividade",
      render: (row) => {
        let value = (row.totalCompleted / row.total) * 100;
        return (
          <Box>
            <Text>{row.totalCompleted + "/" + row.total}</Text>
            <Progress
              value={value}
              size="xs"
              bg="gray.200"
              borderRadius="10"
              colorScheme="gray"
            />
          </Box>
        );
      },
    },
    {
      name: "Tipo",
      key: "tipo",
      render: (row) =>
        ({ WP_TEXT_MSG: "Mensagem Texto", WP_AUDIO_MSG: "Mensagem Áudio" }[
          row.tipo
        ]),
    },
    {
      name: "Status",
      key: "nome",
      render: (row) => {
        const status = getCampanhaStatus(row);
        return (
          <RowStatus status={status.replace(/ /g, "_")}>{status}</RowStatus>
        );
      },
    },
    {
      name: "Ações",
      render: (row) => {
        return (
          <Flex>
            <IconButtonWithLoading
              icon={<SettingsIcon />}
              mr="8px"
              onClick={async () => openEditCampanhaDialog(row)}
            />
            {isPlaying(row) && !isFinalizada(row) && (
              <IconButton
                icon={<FaPause />}
                mr="8px"
                onClick={() => pause(row)}
              />
            )}
            {!isPlaying(row) && !isFinalizada(row) && (
              <IconButton
                disabled={isNotConfigurada(row)}
                icon={<FaPlay />}
                mr="8px"
                onClick={() => play(row)}
              />
            )}
            <IconButton
              icon={<DeleteIcon />}
              variant="danger"
              onClick={() => confirmDeleteCampanha(row)}
            />
          </Flex>
        );
      },
    },
  ];

  return (
    <>
      <GetLayout>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          px="10px"
          mb="30px"
        >
          <Text fontSize="25" fontWeight="bold">
            Campanhas Whatsapp (Nova)
          </Text>

          <Flex gap="10px" alignItems="flex-end">
            <Box w="320px">
              <Text mb="8px">Link prospecção</Text>
              <Flex>
                <Flex
                  border="1px solid var(--chakra-colors-custom-gray)"
                  borderRadius="0.375rem 0 0 0.375rem"
                  w="100%"
                >
                  <InputField
                    value={link}
                    inputProps={{ border: "none !important" }}
                  />
                </Flex>
                <IconButtonWithLoading
                  tooltipMessage="Copiar link"
                  aria-label="Copiar link"
                  icon={<CopyIcon />}
                  h="34px"
                  borderRadius="0 0.375rem 0.375rem 0"
                  isLoading={loadingLink}
                  onClick={async () => {
                    await navigator.clipboard.writeText(link);
                    Toast({
                      title: "Link copiado com sucesso!",
                      status: "info",
                    });
                  }}
                />
              </Flex>
            </Box>
            <Button
              leftIcon={<RepeatIcon />}
              onClick={atualizar}
              isLoading={loading}
              loadingText="Atualizando"
            >
              Atualizar
            </Button>
            <Button
              leftIcon={<AddIcon />}
              onClick={() => openModalCampanhaWhatsApp()}
            >
              Enviar Novo Arquivo
            </Button>
          </Flex>
        </Flex>
        <DynamicTable columns={columns} rows={campanhas} isLoading={loading} />

        <ModalCampanhaWhatsApp
          setCampanhas={setCampanhas}
          loadCampanhas={loadCampanhas}
          getCampanhaStatus={getCampanhaStatus}
        />
      </GetLayout>
    </>
  );
};
